/**
 * Advisory editorial-review flags, surfaced at the publish gate.
 *
 * The editorial review (stage 6b / refresh 11b) never pauses a build — its
 * human_review flags ride along to the point where a human is already
 * reviewing the draft: the publish panel. Renders nothing when there are no
 * flags for a human.
 */
import React from 'react';
import { useArticleContext } from '../ArticleContext';

type ReviewFlag = {
  severity?: string;
  action?: string;
  category?: string;
  quote?: string;
  issue?: string;
};

export function EditorialReviewBanner() {
  const { document } = useArticleContext();
  const review = (document?.preflight as any)?.review;
  const flags: ReviewFlag[] = Array.isArray(review?.flags) ? review.flags : [];
  const humanFlags = flags.filter((f) => f.action === 'human_review');
  if (humanFlags.length === 0) return null;

  const high = humanFlags.filter((f) => f.severity === 'high');
  const rest = humanFlags.length - high.length;

  return (
    <div className={`text-xs rounded border p-2 space-y-1 ${
      high.length > 0 ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-700'
    }`}>
      <div className="font-medium">
        Editorial review: {high.length > 0 ? `${high.length} high-severity flag${high.length === 1 ? '' : 's'}` : ''}
        {high.length > 0 && rest > 0 ? ' + ' : ''}
        {rest > 0 ? `${rest} other flag${rest === 1 ? '' : 's'}` : ''} for review before publishing.
      </div>
      {high.map((f, i) => (
        <div key={i}>
          <span className="font-medium">{f.category || 'issue'}:</span> {f.issue}
          {f.quote && <span className="text-red-600"> — “{f.quote.slice(0, 120)}{(f.quote.length || 0) > 120 ? '…' : ''}”</span>}
        </div>
      ))}
      <div className="opacity-70">
        Advisory only — the build was not paused. Full details on the Editorial Review stage; edit the article via the document editor if needed.
      </div>
    </div>
  );
}
